import domainsConfig from '@/config/domains.json';

export interface DomainConfig {
  domainName: string;
  contactEmail: string;
  companyName: string;
  companyWebsite: string;
}

interface DomainsConfigFile {
  default: string;
  domains: {
    [key: string]: DomainConfig;
  };
}

/**
 * Get current hostname from environment override or window
 */
function getCurrentHostname(): string {
  // Priority 1: Environment variable override
  const envOverride = import.meta.env.VITE_DOMAIN_OVERRIDE;
  if (envOverride) {
    return envOverride;
  }

  // Priority 2: Client-side detection
  return window.location.hostname;
}

/**
 * Get domain configuration by hostname
 */
function getDomainConfigByHostname(hostname: string): DomainConfig {
  const config = domainsConfig as DomainsConfigFile;
  
  // Try to find config for current hostname
  let domainConfig = config.domains[hostname];
  
  // Fallback to default domain if not found
  if (!domainConfig) {
    console.warn(`No configuration found for hostname: ${hostname}, using default: ${config.default}`);
    domainConfig = config.domains[config.default];
  }

  // Still not found? Use first available domain
  if (!domainConfig) {
    const firstDomain = Object.keys(config.domains)[0];
    console.error(`Default domain not found, using first available: ${firstDomain}`);
    domainConfig = config.domains[firstDomain];
  }

  // Apply environment variable overrides
  return {
    domainName: import.meta.env.VITE_DOMAIN_NAME || domainConfig.domainName,
    contactEmail: import.meta.env.VITE_CONTACT_EMAIL || domainConfig.contactEmail,
    companyName: domainConfig.companyName,
    companyWebsite: domainConfig.companyWebsite,
  };
}

/**
 * Get domain configuration based on current hostname
 */
export function getDomainConfig(): DomainConfig {
  const hostname = getCurrentHostname();
  return getDomainConfigByHostname(hostname);
}

/**
 * Get Turnstile configuration from environment variables
 */
export function getTurnstileConfig() {
  return {
    siteKey: import.meta.env.VITE_TURNSTILE_SITE_KEY || '',
  };
}
